import { Prisma } from "@prisma/client";
import { BuyableItemEvent, BuyableItemHandler } from "../buyable-item.types";

type PartialAdvancedStatisticsBuyableItem = {
  advancedStatisticsEvent?: { id: string } | null;
};

type AdvancedStatisticsBuyableItem = {
  advancedStatisticsEvent: { id: string };
};

type AdvancedStatisticsWithEvent = {
  advancedStatisticsEvent: BuyableItemEvent;
};

const GROUP_KEY_PREFIX = "advancedStatistics";

export const AdvancedStatisticsBuyableItemHandler = {
  displayName: "Statistiques avancées",
  accounting: {
    generalAccountCode: "706503",
    invoiceLabel: "Statistiques avancées",
  },

  getEvent(item: AdvancedStatisticsWithEvent): BuyableItemEvent {
    return item.advancedStatisticsEvent;
  },

  isValid(item: PartialAdvancedStatisticsBuyableItem): boolean {
    return !!item.advancedStatisticsEvent?.id;
  },

  findEquivalent(item: PartialAdvancedStatisticsBuyableItem): Prisma.BuyableItemWhereInput {
    return {
      advancedStatisticsEvent: {
        id: item.advancedStatisticsEvent?.id,
      },
    };
  },

  create({
    advancedStatisticsEvent,
  }: AdvancedStatisticsBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      advancedStatisticsEvent: {
        connect: {
          id: advancedStatisticsEvent.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!AdvancedStatisticsBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for advanced statistics: ${groupKey}`);
    }

    const [, advancedStatisticsEventId] = groupKey.split("|");
    return {
      advancedStatisticsEvent: {
        id: advancedStatisticsEventId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialAdvancedStatisticsBuyableItem): string {
    const eventId = item.advancedStatisticsEvent?.id;
    if (!eventId) {
      throw new Error("Cannot get group key for invalid advancedStatistics item");
    }
    return `${GROUP_KEY_PREFIX}|${eventId}`;
  },

  getPricingQuery(): Prisma.PricingFindFirstArgs {
    return {
      where: {
        backofficePrestation: {
          type: "advancedStatistics",
        },
      },
    };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      advancedStatisticsEvent: {
        name: { contains: searchText, mode: "insensitive" },
      },
    };
  },
} as const satisfies BuyableItemHandler;
